import { Create, Delete, Save, SaveAs } from "@mui/icons-material";
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Divider,
	FormControlLabel,
	Grid2,
	TextField,
	Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ErrorModal } from "../../../../../../components/modals/error-modal";
import MesfModal from "../../../../../../components/modals/modal.mesf";
import { NotificationSnackBar } from "../../../../../../controls/snackbars/notification-snackbar";
import { useTrendingContextV2 } from "../../context/TrendingContextV2";
import type { TagFromTree } from "../../models/TrendingModelsV2";
import {
	deleteAllViewTagsFromView,
	upsertView,
	upsertViewTag,
} from "../../repository/TrendingRepositoryV2";
import {
	getViewFromLocal,
	saveViewToLocal,
} from "../../utils/LocalStorageUtils";
import { createViewTagFromTree } from "../../utils/createViewTagFromTree";
import { getViewNameError } from "../../utils/viewNameValidation";
import { SelectedTagsListV2 } from "../SelectedTagsListV2";
import { TagsTreeV2 } from "../TagsTreeV2";
import { SaveUpdateDeleteViewModalV2 } from "./SaveUpdateDeleteViewModalV2";

const CONTENT_HEIGHT = 560;
const TREE_HEIGHT = 490;
const TAGS_LIST_HEIGHT = 300;

interface Props {
	open: boolean;
	handleClose: () => void;
}

export const ManageTagsModalV2 = ({ open, handleClose }: Props) => {
	const queryClient = useQueryClient();

	const {
		state: { viewTags, viewSelected, views },
		actions: { setViewTagsAndRefetch, setViewSelected },
	} = useTrendingContextV2();

	const [viewName, setViewName] = useState("");
	const [isPublic, setIsPublic] = useState(true);
	const [nameError, setNameError] = useState("");
	const [isSaving, setIsSaving] = useState(false);
	const [savedTick, setSavedTick] = useState(0);
	const [viewModalMode, setViewModalMode] = useState<
		"create" | "update" | "delete" | null
	>(null);
	const [openedWithTagIds, setOpenedWithTagIds] = useState<number[]>([]);
	const [notice, setNotice] = useState<string | null>(null);
	const [error, setError] = useState("");

	useEffect(() => {
		if (!open) return;

		setIsSaving(false);
		setViewModalMode(null);
		setNotice(null);
		setError("");
	}, [open]);

	useEffect(() => {
		if (!open) return;

		setViewName(viewSelected?.ViewName ?? "");
		setIsPublic(viewSelected?.IsPublic ?? true);
		setNameError("");
		setOpenedWithTagIds(
			Object.values(viewTags)
				.sort((a, b) => b.order - a.order)
				.map(({ viewTag }) => viewTag.TagId),
		);
	}, [open, viewSelected]);

	const selectedTags = useMemo(() => {
		const listed = openedWithTagIds.filter((tagId) => viewTags[tagId]);
		const added = Object.values(viewTags)
			.filter(({ viewTag }) => !openedWithTagIds.includes(viewTag.TagId))
			.sort((a, b) => a.order - b.order)
			.map(({ viewTag }) => viewTag.TagId);

		return [...listed, ...added].map((tagId) => {
			const { viewTag } = viewTags[tagId];

			return {
				TagId: viewTag.TagId,
				TagName: viewTag.TagName,
				Alias: viewTag.Alias,
				TagTypeCode: viewTag.TagType,
			};
		});
	}, [viewTags, openedWithTagIds]);

	const handleTagSelect = useCallback(
		(tag: TagFromTree) => {
			if (viewTags[tag.TagId]) {
				setNotice(`${tag.TagName} is already in the list`);
				return;
			}

			const nextOrder =
				Math.max(...Object.values(viewTags).map(({ order }) => order), 0) + 1;

			setViewTagsAndRefetch({
				...viewTags,
				[tag.TagId]: { viewTag: createViewTagFromTree(tag), order: nextOrder },
			});
		},
		[viewTags, setViewTagsAndRefetch],
	);

	const handleRemoveTag = useCallback(
		(tagId: number) => {
			const { [tagId]: removed, ...remaining } = viewTags;
			setViewTagsAndRefetch(remaining);
		},
		[viewTags, setViewTagsAndRefetch],
	);

	const handleViewNameChange = (value: string) => {
		setViewName(value);
		setNameError("");
	};

	const persistedTags = useMemo(
		() => (viewSelected ? getViewFromLocal(viewSelected.ViewId) : null),
		[viewSelected, savedTick],
	);

	const hasTags = selectedTags.length > 0;
	const trimmedName = viewName.trim();

	const tagsChanged = useMemo(() => {
		if (!viewSelected) return hasTags;
		if (!persistedTags) return true;

		return JSON.stringify(viewTags) !== JSON.stringify(persistedTags);
	}, [viewSelected, persistedTags, viewTags, hasTags]);

	const viewNameDisabled = isSaving || !!viewSelected;
	const isPublicDisabled = viewNameDisabled || (!viewSelected && !trimmedName);

	const saveViewDisabled = isSaving || !!viewSelected || !trimmedName;
	const saveTagsDisabled = isSaving || !viewSelected || !tagsChanged;

	const persistViewTags = async (targetViewId: number) => {
		const backendTags = Object.values(viewTags)
			.filter(({ viewTag }) => !viewTag.IsBitExtracted)
			.sort((a, b) => a.order - b.order);

		for (const { viewTag, order } of backendTags) {
			await upsertViewTag({
				viewId: targetViewId,
				tagId: viewTag.TagId,
				color: viewTag.Color,
				minScale: viewTag.MinScale,
				maxScale: viewTag.MaxScale,
				isVisible: viewTag.IsVisible,
				isAutoScale: viewTag.IsAutoScale,
				unit: viewTag.Unit,
				order,
				alias: viewTag.Alias,
				tagType: viewTag.TagType,
				sourceTagId: viewTag.SourceTagId,
				bitIndex: viewTag.BitIndex,
			});
		}

		saveViewToLocal(targetViewId, viewTags);
	};

	const handleSaveView = async () => {
		const validationError = getViewNameError(viewName, views, null);
		if (validationError) {
			setNameError(validationError);
			return;
		}

		setIsSaving(true);
		try {
			const targetViewId = await upsertView({
				viewId: null,
				viewName: trimmedName,
				isPublic,
			});

			if (!targetViewId) {
				throw new Error("The view was not created");
			}

			setViewSelected({
				ViewId: targetViewId,
				ViewName: trimmedName,
				IsPublic: isPublic,
			});
			await queryClient.invalidateQueries({ queryKey: ["views-v2"] });

			setSavedTick((tick) => tick + 1);
			setNotice(`${trimmedName} was saved`);
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		} finally {
			setIsSaving(false);
		}
	};

	const handleSaveTags = async () => {
		if (!viewSelected) return;

		setIsSaving(true);
		try {
			await deleteAllViewTagsFromView(viewSelected.ViewId);
			await persistViewTags(viewSelected.ViewId);

			setSavedTick((tick) => tick + 1);
			setNotice(`Tags saved to ${viewSelected.ViewName}`);
			handleClose();
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<>
			<MesfModal
				open={open}
				handleClose={handleClose}
				maxWidth="xl"
				title="TAGS"
			>
				<MesfModal.Content
					dividers
					style={{ padding: "15px 20px", width: "1120px", maxWidth: "100%" }}
				>
					<Grid2 container spacing={2} sx={{ height: CONTENT_HEIGHT }}>
						<Grid2 size={7} sx={{ minWidth: 0, overflowX: "auto" }}>
							<TagsTreeV2
								onTagSelect={handleTagSelect}
								treeHeight={TREE_HEIGHT}
								minWidth={0}
								minHeight={0}
								notifyOnSearchSelect={false}
							/>
						</Grid2>

						<Grid2
							size={5}
							sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}
						>
							<Typography
								variant="caption"
								color="textSecondary"
								fontWeight={600}
							>
								View settings
							</Typography>

							<Box
								sx={{
									display: "flex",
									flexWrap: "wrap",
									alignItems: "center",
									gap: 1,
									mt: 0.5,
								}}
							>
								<Button
									variant="contained"
									color="primary"
									size="small"
									startIcon={
										isSaving ? <CircularProgress size="1rem" /> : <Save />
									}
									disabled={saveViewDisabled}
									onClick={handleSaveView}
								>
									Save
								</Button>
								<Button
									variant="outlined"
									color="primary"
									size="small"
									startIcon={<SaveAs />}
									disabled={!viewSelected || isSaving}
									onClick={() => setViewModalMode("create")}
								>
									Save As
								</Button>
								<Button
									variant="outlined"
									color="primary"
									size="small"
									startIcon={<Create />}
									disabled={!viewSelected || isSaving}
									onClick={() => setViewModalMode("update")}
								>
									Rename
								</Button>

								<Box sx={{ flex: 1 }} />

								<Button
									variant="outlined"
									color="secondary"
									size="small"
									startIcon={<Delete />}
									disabled={!viewSelected || isSaving}
									onClick={() => setViewModalMode("delete")}
								>
									Delete
								</Button>
							</Box>

							<Box
								sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 1 }}
							>
								<TextField
									label="View Name"
									variant="outlined"
									value={viewName}
									onChange={(e) => handleViewNameChange(e.target.value)}
									error={!!nameError}
									helperText={nameError}
									disabled={viewNameDisabled}
									size="small"
									autoComplete="off"
									sx={{ flex: 1, minWidth: 0 }}
								/>
								<FormControlLabel
									sx={{ flexShrink: 0, mr: 0, mt: 0.5 }}
									control={
										<Checkbox
											checked={isPublic}
											onChange={(e) => setIsPublic(e.target.checked)}
											disabled={isPublicDisabled}
											color="primary"
											size="small"
										/>
									}
									label="Public"
								/>
							</Box>

							<Divider sx={{ mt: 1.5 }} />

							<Typography
								variant="caption"
								color="textSecondary"
								fontWeight={600}
								sx={{ mt: 1 }}
							>
								Tag settings
							</Typography>

							<Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
								<Button
									variant="contained"
									color="primary"
									size="small"
									startIcon={
										isSaving ? <CircularProgress size="1rem" /> : <Save />
									}
									disabled={saveTagsDisabled}
									onClick={handleSaveTags}
								>
									Save
								</Button>
							</Box>

							{selectedTags.length === 0 ? (
								<Typography
									variant="body2"
									color="textSecondary"
									sx={{ mt: 2, textAlign: "center" }}
								>
									Click a tag on the tree to add it to the chart
								</Typography>
							) : (
								<SelectedTagsListV2
									tags={selectedTags}
									onRemove={handleRemoveTag}
									maxHeight={TAGS_LIST_HEIGHT}
								/>
							)}

						</Grid2>
					</Grid2>
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
					<Grid2 container spacing={2} justifyContent="flex-end">
						<Grid2 size={{ md: 3, xs: 12 }}>
							<Button
								fullWidth
								variant="contained"
								color="inherit"
								onClick={handleClose}
								disabled={isSaving}
							>
								Close
							</Button>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>

			{viewModalMode && (
				<SaveUpdateDeleteViewModalV2
					open={true}
					mode={viewModalMode}
					view={viewModalMode === "create" ? null : viewSelected}
					handleClose={async (shouldUpdate) => {
						const deletedCurrentView =
							viewModalMode === "delete" && shouldUpdate && !!viewSelected;

						setViewModalMode(null);

						if (deletedCurrentView) {
							setViewSelected(null);
							setViewTagsAndRefetch({});
						}

						if (shouldUpdate) {
							await queryClient.invalidateQueries({ queryKey: ["views-v2"] });
							handleClose();
						}
					}}
				/>
			)}

			<NotificationSnackBar
				message={notice}
				severity="info"
				onHide={() => setNotice(null)}
			/>

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
